import {useState, useEffect, useCallback} from 'react';
import Cookies from 'js-cookie';
import {useUserMe} from 'data/user';
import {useCurrentOrganization, getCurrentOrganizationIdCookie} from 'data/organization';
import {useApiKeys} from 'data/apiKey';
import {removeCrossSiteCookie, setCrossSiteCookie} from 'utils/cookies';

export const useAuth = () => {
	const [apiToken, setApiToken] = useState(() => Cookies.get('api_token'));
	const [organizationId, setOrganizationId] = useState(() =>
		getCurrentOrganizationIdCookie(),
	);

	const {data: activeOrganization} = useCurrentOrganization({
		useQueryOptions: {
			enabled: !!apiToken && !!organizationId,
			refetchOnWindowFocus: false,
		},
	});

	const {data: user} = useUserMe({
		useQueryOptions: {
			enabled: !!organizationId && !!apiToken,
		},
	});

	const {data: apiKeys} = useApiKeys({
		useQueryOptions: {
			enabled: !!user && !!apiToken,
		},
	});

	// Cookie sync — detects external changes or token loss
	useEffect(() => {
		const syncAuthCookies = () => {
			const currentApiTokenCookie = Cookies.get('api_token');
			const currentOrgIdNumber = getCurrentOrganizationIdCookie();

			const tokenChanged = currentApiTokenCookie !== apiToken;
			const orgChanged = currentOrgIdNumber !== organizationId;

			if (!orgChanged && !tokenChanged) {
				return;
			}

			if (tokenChanged) {
				setApiToken(currentApiTokenCookie);
			}
			if (orgChanged) {
				setOrganizationId(currentOrgIdNumber);
			}

			// If there's no API token but organization_id exists, remove the organization_id
			// This prevents redirect loops when the user has organization_id but no valid token
			if (!currentApiTokenCookie && currentOrgIdNumber) {
				removeCrossSiteCookie('organization_id');
				setOrganizationId(null);
			}
		};

		// Run sync immediately on mount to handle edge cases
		syncAuthCookies();

		const interval = setInterval(syncAuthCookies, 3000);
		window.addEventListener('storage', syncAuthCookies);

		return () => {
			clearInterval(interval);
			window.removeEventListener('storage', syncAuthCookies);
		};
	}, [apiToken, organizationId]);

	const logout = useCallback(() => {
		try {
			removeCrossSiteCookie('api_token');
			removeCrossSiteCookie('organization_id');
		} finally {
			// keep local state in sync immediately (cookie sync interval is 3s)
			setApiToken(null);
			setOrganizationId(null);
			window.location.assign('/');
		}
	}, []);

	const setOrganization = useCallback(id => {
		if (!id) return;
		setCrossSiteCookie('organization_id', id, {expires: 365});
		// keep local state in sync immediately (cookie sync interval is 3s)
		setOrganizationId(Number(id));
		// Full reload to ensure all apps pick up organization switch consistently (legacy behavior)
		window.location.reload();
	}, []);

	return {
		apiToken,
		setApiToken,
		organizationId,
		setOrganizationId,
		activeOrganization,
		user,
		apiKeys,
		logout,
		setOrganization,
	};
};
